import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { AlertTriangle, ArrowLeft } from "lucide-react";
import DNALoader from "../components/DNALoader";
import { getCategory } from "../lib/api";
import SEO, { canonicalFor, titleFor } from "../lib/seo";

const ratingColor = (r) => (r === "red" ? "#FF3B3B" : r === "yellow" ? "#FFB800" : "#00C853");

export default function CategoryDetail() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  useEffect(() => {
    setLoading(true);
    setErr(null);
    getCategory(slug)
      .then(setData)
      .catch((e) => setErr(e?.response?.data?.detail || "Category not found"))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) return <div className="max-w-4xl mx-auto px-6 py-24"><DNALoader label="Loading category..." /></div>;

  if (err)
    return (
      <div className="max-w-3xl mx-auto px-6 py-24 text-center">
        <AlertTriangle className="h-10 w-10 text-[color:var(--danger)] mx-auto mb-4" />
        <h2 className="font-display text-2xl font-semibold mb-2">Not found</h2>
        <p className="text-[color:var(--text-2)] mb-6">{err}</p>
        <Link to="/categories" className="lime-btn rounded-lg px-4 py-2 text-sm">All categories</Link>
      </div>
    );

  if (!data) return null;

  return (
    <div data-testid="category-detail-page" className="max-w-5xl mx-auto px-6 md:px-10 py-12 md:py-16">
      <SEO
        title={titleFor(`${data.name}: Ingredient Safety Ratings`)}
        description={`${data.description} See ${data.productCount} analyzed ${data.name.toLowerCase()} ranked by safety score.`}
        canonical={canonicalFor(`/category/${slug}`)}
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          name: `${data.name} — Ingredient Safety`,
          description: data.description,
        }}
      />

      <Link to="/categories" data-testid="category-back" className="ghost-btn rounded-lg px-3 py-1.5 text-sm inline-flex items-center gap-2 mb-8">
        <ArrowLeft className="h-4 w-4" /> All categories
      </Link>

      <div className="mb-8">
        <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-[color:var(--text-3)] mb-2">Category</div>
        <h1 data-testid="category-name" className="font-display text-4xl md:text-6xl font-bold tracking-tighter">{data.name}</h1>
        <p className="mt-3 text-[color:var(--text-2)] max-w-2xl leading-relaxed">{data.description}</p>
      </div>

      {data.products.length === 0 ? (
        <div className="surface rounded-xl p-8 text-center text-[color:var(--text-2)]">
          Nothing analyzed in this category yet. Search a product on the home page to fill it in.
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="category-products">
          {data.products.map((p, i) => {
            const color = ratingColor(p.overallRating);
            return (
              <Link
                key={p.slug || i}
                to={`/product/${p.slug}`}
                data-testid={`category-product-${i}`}
                className="surface rounded-xl p-4 block hover:border-[color:var(--lime)]/40 transition-colors"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="font-display font-semibold truncate">{p.productName}</div>
                    <div className="text-xs text-[color:var(--text-3)] truncate">{p.brand || "—"}</div>
                  </div>
                  <span className="font-mono text-2xl font-bold flex-shrink-0" style={{ color }}>
                    {p.overallScore ?? "—"}
                  </span>
                </div>
                {p.overallVerdict && (
                  <p className="text-xs text-[color:var(--text-2)] mt-2 leading-relaxed line-clamp-2">{p.overallVerdict}</p>
                )}
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}
